// rr
import * as React from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Typography,
} from '@mui/material';
import {
  GridFilterPanel,
  type GridSortModel,
  Toolbar,
  ToolbarButton,
  useGridApiContext,
  useGridRootProps,
} from '@mui/x-data-grid-premium';
import { isEmbeddableWidgetBundle } from '@/integrations/embeddableWidgetPortalUtils';
import { ManagedColumnsManagement } from '@/components/table/ManagedColumnsManagement';

type PreferencesPanel = 'columns' | 'filters' | 'sort';

type ColumnLike = {
  field: string;
  headerName?: string;
  sortable?: boolean;
};

const isColumnLike = (v: unknown): v is ColumnLike => {
  return (
    typeof v === 'object' &&
    v !== null &&
    'field' in v &&
    typeof v.field === 'string'
  );
};

// The same parse/format pair MUI's `QuickFilter` applies on the wider toolbars,
// so a filter typed here means what it would mean there and the tokens keep
// their boundaries when a search set elsewhere is edited on this one.
function parseQuickFilter(input: string): string[] {
  return input.split(' ').filter((word) => word !== '');
}

function formatQuickFilter(values: readonly string[] | undefined): string {
  return values?.join(' ') ?? '';
}

export type XsPreferencesDrawerToolbarProps = {
  showColumns?: boolean;
  showSort?: boolean;
  showQuickFilter?: boolean;
  showFilters?: boolean;
  touchFriendly?: boolean;
  exportControl?: React.ReactNode;
};

type ScrollSnapshot = {
  scrollX: number;
  scrollY: number;
  ancestors: Array<{
    element: HTMLElement;
    scrollTop: number;
    scrollLeft: number;
  }>;
};

const captureScrollSnapshot = (container: HTMLElement): ScrollSnapshot => {
  const ancestors: ScrollSnapshot['ancestors'] = [];
  let node: HTMLElement | null = container.parentElement;

  while (node) {
    const { overflowX, overflowY } = window.getComputedStyle(node);
    if (
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflowX === 'auto' ||
      overflowX === 'scroll'
    ) {
      ancestors.push({
        element: node,
        scrollTop: node.scrollTop,
        scrollLeft: node.scrollLeft,
      });
    }
    node = node.parentElement;
  }

  return {
    scrollX: window.scrollX,
    scrollY: window.scrollY,
    ancestors,
  };
};

const restoreScrollSnapshot = (snapshot: ScrollSnapshot): void => {
  window.scrollTo(snapshot.scrollX, snapshot.scrollY);
  for (const { element, scrollTop, scrollLeft } of snapshot.ancestors) {
    element.scrollTop = scrollTop;
    element.scrollLeft = scrollLeft;
  }
};

/**
 * GridColumnsManagement still focuses the first checkbox when
 * autoFocusSearchField is false. In embeddable widgets that focus scrolls the
 * host page before a deferred blur can run — restore scroll synchronously.
 */
function useSuppressColumnsPanelAutofocusScroll(
  containerRef: React.RefObject<HTMLDivElement | null>,
) {
  React.useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const snapshot = isEmbeddableWidgetBundle
      ? captureScrollSnapshot(container)
      : { scrollX: window.scrollX, scrollY: window.scrollY, ancestors: [] };

    const restoreScroll = () => {
      if (isEmbeddableWidgetBundle) {
        restoreScrollSnapshot(snapshot);
        return;
      }
      window.scrollTo(snapshot.scrollX, snapshot.scrollY);
    };

    let suppressProgrammaticFocus = true;
    const stopSuppressing = () => {
      suppressProgrammaticFocus = false;
    };

    container.addEventListener('pointerdown', stopSuppressing, {
      once: true,
      capture: true,
    });

    const neutralizeAutofocus = () => {
      const active = document.activeElement;
      if (active instanceof HTMLElement && container.contains(active)) {
        active.blur();
      }
      restoreScroll();
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (!suppressProgrammaticFocus) {
        return;
      }
      const target = event.target;
      if (!(target instanceof HTMLElement) || !container.contains(target)) {
        return;
      }
      target.blur();
      restoreScroll();
    };

    container.addEventListener('focusin', handleFocusIn, true);
    neutralizeAutofocus();

    const rafId = window.requestAnimationFrame(() => {
      neutralizeAutofocus();
      window.requestAnimationFrame(neutralizeAutofocus);
    });
    const timeoutId = window.setTimeout(stopSuppressing, 500);

    return () => {
      stopSuppressing();
      window.clearTimeout(timeoutId);
      window.cancelAnimationFrame(rafId);
      container.removeEventListener('focusin', handleFocusIn, true);
      container.removeEventListener('pointerdown', stopSuppressing, true);
    };
  }, [containerRef]);
}

/** Columns panel for the mobile drawer — suppress autofocus to avoid page scroll on open. */
function DrawerColumnsManagement() {
  const rootProps = useGridRootProps();
  const containerRef = React.useRef<HTMLDivElement>(null);

  useSuppressColumnsPanelAutofocusScroll(containerRef);

  return (
    <Box ref={containerRef}>
      <ManagedColumnsManagement
        {...rootProps.slotProps?.columnsManagement}
        autoFocusSearchField={false}
      />
    </Box>
  );
}

/**
 * XS-only toolbar that replaces the built-in Columns/Filters preference "modals"
 * with bottom-sheet drawers. Designed to be used as `slots.toolbar` on XS.
 */
export default function XsPreferencesDrawerToolbar({
  showColumns = true,
  showSort = true,
  showQuickFilter = false,
  showFilters = true,
  touchFriendly = false,
  exportControl,
}: XsPreferencesDrawerToolbarProps) {
  const apiRef = useGridApiContext();
  const { t } = useLocalization();

  const [openPanel, setOpenPanel] = React.useState<PreferencesPanel | null>(
    null,
  );
  const [exportAnchorEl, setExportAnchorEl] =
    React.useState<HTMLElement | null>(null);
  const restoredQuickFilter = formatQuickFilter(
    apiRef.current.state.filter.filterModel.quickFilterValues,
  );
  const [isSearchOpen, setIsSearchOpen] = React.useState(
    () => restoredQuickFilter !== '',
  );
  const [searchValue, setSearchValue] = React.useState(
    () => restoredQuickFilter,
  );
  const [sortModel, setSortModel] = React.useState<GridSortModel>(
    () => apiRef.current.state.sorting.sortModel,
  );

  // The quick filter can also be set from outside the toolbar (the owning page
  // syncing the grid to its `?search=` params), so the input follows the grid's
  // filter model rather than owning it alone.
  React.useEffect(() => {
    return apiRef.current.subscribeEvent('filterModelChange', (model) => {
      const next = formatQuickFilter(model.quickFilterValues);
      // Only adopt a model this input did not produce: the raw text is kept
      // otherwise, so typing a separator is not undone mid-word.
      setSearchValue((current) =>
        formatQuickFilter(parseQuickFilter(current)) === next ? current : next,
      );
      if (next) setIsSearchOpen(true);
    });
  }, [apiRef]);

  const touchTargetStyles = touchFriendly
    ? { minHeight: 44, minWidth: 44 }
    : undefined;

  const closeDrawer = () => {
    setOpenPanel(null);
  };
  const isExportMenuOpen = Boolean(exportAnchorEl);
  const closeExportMenu = () => {
    setExportAnchorEl(null);
  };

  const getExportFields = React.useCallback(() => {
    const allColumns = apiRef.current.getAllColumns();
    const visibilityModel = apiRef.current.state.columns.columnVisibilityModel;

    return allColumns
      .filter(isColumnLike)
      .map((c) => c.field)
      .filter(
        (field) =>
          field !== '' &&
          field !== 'listColumn' &&
          field !== '__list__' &&
          field !== 'actions' &&
          !field.startsWith('__') &&
          (visibilityModel[field] ?? true),
      );
  }, [apiRef]);

  const openDrawer = (panel: PreferencesPanel) => {
    // Ensure the DataGrid built-in preferences panel (if open) doesn't compete with the drawer.
    apiRef.current.hidePreferences();
    setOpenPanel(panel);
  };

  React.useEffect(() => {
    const unsubscribe = apiRef.current.subscribeEvent(
      'sortModelChange',
      (model: GridSortModel) => {
        setSortModel(model);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [apiRef]);

  return (
    <>
      <Toolbar
        style={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {showColumns ? (
          <ToolbarButton
            aria-label={t('grid.columns', 'Columns')}
            style={touchTargetStyles}
            onClick={() => {
              openDrawer('columns');
            }}
          >
            <ViewColumnIcon fontSize='small' />
          </ToolbarButton>
        ) : null}

        {showSort ? (
          <ToolbarButton
            aria-label={t('grid.sort', 'Sort')}
            style={touchTargetStyles}
            onClick={() => {
              openDrawer('sort');
            }}
          >
            <SwapVertIcon fontSize='small' />
          </ToolbarButton>
        ) : null}

        {showFilters ? (
          <ToolbarButton
            aria-label={t('filtersToolbar', 'Filters')}
            style={touchTargetStyles}
            onClick={() => {
              openDrawer('filters');
            }}
          >
            <FilterListIcon fontSize='small' />
          </ToolbarButton>
        ) : null}

        {exportControl !== undefined ? (
          exportControl
        ) : (
          <>
            <ToolbarButton
              aria-label={t('export', 'Export')}
              style={touchTargetStyles}
              onClick={(e) => {
                setExportAnchorEl(e.currentTarget);
              }}
            >
              <FileDownloadIcon fontSize='small' />
            </ToolbarButton>

            <Menu
              anchorEl={exportAnchorEl}
              open={isExportMenuOpen}
              onClose={closeExportMenu}
            >
              <MenuItem
                onClick={() => {
                  apiRef.current.exportDataAsCsv({
                    fields: getExportFields(),
                  });
                  closeExportMenu();
                }}
              >
                CSV
              </MenuItem>
              <MenuItem
                onClick={() => {
                  void apiRef.current.exportDataAsExcel({
                    fields: getExportFields(),
                  });
                  closeExportMenu();
                }}
              >
                Excel
              </MenuItem>
            </Menu>
          </>
        )}

        {showQuickFilter ? (
          <ToolbarButton
            aria-label={t('grid.search', 'Search')}
            style={touchTargetStyles}
            onClick={() => {
              setIsSearchOpen((prev) => !prev);
              if (isSearchOpen) {
                // Closing search clears the quick filter.
                setSearchValue('');
                apiRef.current.setQuickFilterValues([]);
              }
            }}
          >
            {isSearchOpen ? (
              <CancelIcon fontSize='small' />
            ) : (
              <SearchIcon fontSize='small' />
            )}
          </ToolbarButton>
        ) : null}
      </Toolbar>

      {showQuickFilter && isSearchOpen ? (
        <Box sx={{ px: 1, pt: 1, width: '100%' }}>
          <TextField
            value={searchValue}
            onChange={(e) => {
              const next = e.target.value;
              setSearchValue(next);
              apiRef.current.setQuickFilterValues(parseQuickFilter(next));
            }}
            autoFocus
            fullWidth
            size='small'
            placeholder={t('grid.search', 'Search')}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' />
                  </InputAdornment>
                ),
                endAdornment: searchValue ? (
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label={t('clear', 'Clear')}
                      edge='end'
                      size='small'
                      onClick={() => {
                        setSearchValue('');
                        apiRef.current.setQuickFilterValues([]);
                      }}
                    >
                      <CancelIcon fontSize='small' />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
          />
        </Box>
      ) : null}

      <Drawer
        anchor='bottom'
        open={openPanel !== null}
        onClose={closeDrawer}
        ModalProps={{
          keepMounted: true,
          disableScrollLock: true,
          disableAutoFocus: true,
          disableEnforceFocus: true,
          disableRestoreFocus: true,
        }}
        className='mtool-drawer'
      >
        {/* Bottom-sheet handle */}
        <Box
          sx={{
            pt: 1,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 999,
              bgcolor: 'divider',
            }}
          />
        </Box>

        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant='h6'
            sx={{ flex: 1 }}
          >
            {openPanel === 'columns'
              ? t('grid.columns', 'Columns')
              : openPanel === 'sort'
                ? t('grid.sort', 'Sort')
                : t('filtersToolbar', 'Filters')}
          </Typography>

          <IconButton
            aria-label={t('close', 'Close')}
            onClick={closeDrawer}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            px: 2,
            pb: 2,
            boxSizing: 'border-box',
            '& .MuiDataGrid-filterForm': {
              display: 'block',
              width: '100%',
            },
            '& .MuiDataGrid-filterFormLogicOperatorInput': {
              marginBottom: '40px !important',
            },
            '& .MuiFormControl-root, .MuiInputBase-root': {
              display: 'block',
              width: '100%',
              marginBottom: '20px',
            },
            '& .MuiDataGrid-filterFormValueInput': {
              display: 'block',
              width: '100%',
              marginBottom: '40px',
            },
            '& .MuiDataGrid-columnsManagementSearchInput .MuiInputBase-root': {
              marginBottom: '0px !important',
              display: 'flex !important',
            },
            '& .MuiDataGrid-filterFormValueInput input': {
              padding: '24px 12px',
              boxSizing: 'border-box',
            },
            '& .MuiDataGrid-filterFormDeleteIcon': {
              display: 'none',
            },
          }}
        >
          {openPanel === 'columns' ? (
            <DrawerColumnsManagement />
          ) : openPanel === 'sort' ? (
            <Box sx={{ pb: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mb: 1,
                }}
              >
                <Button
                  onClick={() => {
                    apiRef.current.setSortModel([]);
                  }}
                  size='small'
                >
                  {t('grid.clearSort', 'Clear sort')}
                </Button>
              </Box>

              <List
                dense
                disablePadding
              >
                {(() => {
                  const rawColumns = apiRef.current.getAllColumns();
                  const sortableColumns = rawColumns
                    .filter(isColumnLike)
                    .map((c) => ({
                      field: c.field,
                      headerName: c.headerName ?? c.field,
                      sortable: c.sortable !== false,
                    }))
                    .filter((c) => c.field !== '')
                    .filter((c) => c.sortable)
                    .filter((c) => c.field !== 'actions')
                    .filter((c) => c.field !== 'listColumn')
                    .filter((c) => !c.field.startsWith('__'));

                  const getSortDir = (field: string) =>
                    sortModel.find((m) => m.field === field)?.sort ?? null;

                  const cycleSort = (field: string) => {
                    const current = getSortDir(field);
                    const next =
                      current === null
                        ? 'asc'
                        : current === 'asc'
                          ? 'desc'
                          : null;
                    apiRef.current.setSortModel(
                      next ? [{ field, sort: next }] : [],
                    );
                  };

                  return sortableColumns.map((col) => {
                    const field = col.field;
                    const label = col.headerName || field;
                    const dir = getSortDir(field);
                    const hintText =
                      dir === 'asc'
                        ? t('grid.sortedAsc', 'Sorted Asc')
                        : dir === 'desc'
                          ? t('grid.sortedDesc', 'Sorted Desc')
                          : t('grid.notSorted', 'Not sorted');

                    return (
                      <ListItemButton
                        key={field}
                        onClick={() => {
                          cycleSort(field);
                        }}
                        sx={{
                          px: 1,
                          borderRadius: 1,
                          '& .sortHint': {
                            opacity: dir ? 1 : 0,
                            transition: 'opacity 120ms',
                          },
                          '&:hover .sortHint': { opacity: 1 },
                        }}
                      >
                        <ListItemText
                          primary={label}
                          slotProps={{ primary: { variant: 'body2' } }}
                        />

                        <Box
                          className='sortHint'
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            ml: 1,
                            color: 'text.secondary',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {dir === 'asc' ? (
                            <ArrowUpwardIcon fontSize='inherit' />
                          ) : dir === 'desc' ? (
                            <ArrowDownwardIcon fontSize='inherit' />
                          ) : null}
                          <Typography
                            variant='caption'
                            color='text.secondary'
                          >
                            {hintText}
                          </Typography>
                        </Box>
                      </ListItemButton>
                    );
                  });
                })()}
              </List>
            </Box>
          ) : showFilters ? (
            <GridFilterPanel />
          ) : null}
        </Box>

        {openPanel === 'filters' && showFilters ? (
          <Box
            sx={{
              px: 2,
              pb: 2,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              onClick={() => {
                apiRef.current.setFilterModel({ items: [] });
              }}
            >
              {t('grid.clearFilters', 'Clear filters')}
            </Button>
          </Box>
        ) : null}
      </Drawer>
    </>
  );
}
